import { createGlobalStyle } from "styled-components";

export const Styles = createGlobalStyle`

    @font-face {
        font-family: "Motiva Sans Light";
        src: url("/fonts/Motiva-Sans-Light.ttf") format("truetype");
        font-style: normal;
    }

    @font-face {
        font-family: "Motiva Sans Bold";
        src: url("/fonts/Motiva-Sans-Bold.ttf") format("truetype");
        font-style: normal;
    }

    /* CSS Custom Properties for Design System */
    :root {
        /* Primary Blue Palette */
        --primary-color: #1e40af;
        --primary-light: #3b82f6;
        --primary-dark: #1e3a8a;
        --secondary-color: #2563eb;
        --accent-color: #60a5fa;
        
        /* Brand Blue Variations */
        --blue-900: #1e3a8a;
        --blue-800: #1e40af;
        --blue-700: #1d4ed8;
        --blue-600: #2563eb;
        --blue-500: #3b82f6;
        --blue-400: #60a5fa;
        --blue-300: #93c5fd;
        --blue-200: #bfdbfe;
        --blue-100: #dbeafe;
        --blue-50: #eff6ff;
        
        /* Grays */
        --gray-50: #f9fafb;
        --gray-100: #f3f4f6;
        --gray-200: #e5e7eb;
        --gray-300: #d1d5db;
        --gray-400: #9ca3af;
        --gray-500: #6b7280;
        --gray-600: #4b5563;
        --gray-700: #374151;
        --gray-800: #1f2937;
        --gray-900: #111827;
        
        /* Typography */
        --font-primary: 'Motiva Sans Light', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        --font-heading: 'Motiva Sans Bold', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        
        /* Font Sizes */
        --text-xs: 0.75rem;
        --text-sm: 0.875rem;
        --text-base: 1rem;
        --text-lg: 1.125rem;
        --text-xl: 1.25rem;
        --text-2xl: 1.5rem;
        --text-3xl: 1.875rem;
        --text-4xl: 2.25rem;
        --text-5xl: 3rem;
        --text-6xl: 3.75rem;
        
        /* Spacing */
        --space-1: 0.25rem;
        --space-2: 0.5rem;
        --space-3: 0.75rem;
        --space-4: 1rem;
        --space-5: 1.25rem;
        --space-6: 1.5rem;
        --space-8: 2rem;
        --space-10: 2.5rem;
        --space-12: 3rem;
        --space-16: 4rem;
        --space-20: 5rem;
        --space-24: 6rem;
        
        /* Shadows */
        --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
        --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
        --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
        --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
        
        /* Border Radius */
        --radius-sm: 0.375rem;
        --radius-md: 0.5rem;
        --radius-lg: 0.75rem;
        --radius-xl: 1rem;
        --radius-2xl: 1.5rem;
        
        /* Layout */
        --max-width: 1200px;
        --container-padding: 1.5rem;
        
        /* Transitions */
        --transition-fast: 0.15s ease-in-out;
        --transition-normal: 0.3s ease-in-out;
        --transition-slow: 0.5s ease-in-out;
    }

    /* Reset and Base Styles */
    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    html {
        scroll-behavior: smooth;
        line-height: 1.15;
        -webkit-text-size-adjust: 100%;
    }

    body {
        margin: 0;
        padding: 0;
        font-family: var(--font-primary);
        font-size: var(--text-base);
        line-height: 1.6;
        color: var(--gray-700);
        background-color: #ffffff;
        overflow-x: hidden;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    /* Typography Hierarchy */
    h1, h2, h3, h4, h5, h6 {
        font-family: var(--font-heading);
        font-weight: 700;
        line-height: 1.2;
        color: var(--primary-color);
        margin: 0 0 var(--space-4) 0;
        letter-spacing: -0.025em;
    }

    h1 {
        font-size: var(--text-5xl);
        font-weight: 800;
        letter-spacing: -0.05em;
        
        @media (max-width: 768px) {
            font-size: var(--text-4xl);
        }
        
        @media (max-width: 480px) {
            font-size: var(--text-3xl);
        }
    }

    h2 {
        font-size: var(--text-4xl);
        
        @media (max-width: 768px) {
            font-size: var(--text-3xl);
        }
        
        @media (max-width: 480px) {
            font-size: var(--text-2xl);
        }
    }

    h3 {
        font-size: var(--text-3xl);
        
        @media (max-width: 768px) {
            font-size: var(--text-2xl);
        }
        
        @media (max-width: 480px) {
            font-size: var(--text-xl);
        }
    }

    h4 {
        font-size: var(--text-2xl);
        
        @media (max-width: 768px) {
            font-size: var(--text-xl);
        }
    }

    h5 {
        font-size: var(--text-xl);
        
        @media (max-width: 768px) {
            font-size: var(--text-lg);
        }
    }

    h6 {
        font-size: var(--text-lg);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-weight: 600;
        color: var(--gray-600);
    }

    p {
        font-size: var(--text-lg);
        line-height: 1.7;
        color: var(--gray-600);
        margin: 0 0 var(--space-4) 0;
        
        @media (max-width: 768px) {
            font-size: var(--text-base);
        }
    }

    /* Lead text for hero sections */
    .lead {
        font-size: var(--text-xl);
        line-height: 1.6;
        color: var(--gray-500);
        
        @media (max-width: 768px) {
            font-size: var(--text-lg);
        }
    }

    /* Small text */
    .text-sm {
        font-size: var(--text-sm);
        line-height: 1.5;
    }

    /* Links */
    a {
        color: var(--primary-color);
        text-decoration: none;
        transition: color var(--transition-fast);
        
        &:hover {
            color: var(--primary-light);
        }
        
        &:focus {
            outline: 2px solid var(--accent-color);
            outline-offset: 2px;
        }
    }

    /* Form Elements */
    input,
    textarea,
    select {
        font-family: inherit;
        font-size: var(--text-base);
        line-height: 1.5;
        color: var(--gray-700);
        background-color: #ffffff;
        border: 1px solid var(--gray-300);
        border-radius: var(--radius-md);
        padding: var(--space-3) var(--space-4);
        width: 100%;
        transition: all var(--transition-normal);
        
        &:focus {
            outline: none;
            border-color: var(--primary-color);
            box-shadow: 0 0 0 3px rgb(24 33 109 / 0.1);
        }
        
        &::placeholder {
            color: var(--gray-400);
        }
    }

    textarea {
        resize: vertical;
        min-height: 120px;
    }

    /* Buttons */
    button {
        font-family: inherit;
        cursor: pointer;
        transition: all var(--transition-normal);
        border: none;
        border-radius: var(--radius-md);
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        
        &:focus {
            outline: 2px solid var(--accent-color);
            outline-offset: 2px;
        }
        
        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    /* Lists */
    ul, ol {
        margin: 0 0 var(--space-4) 0;
        padding-left: var(--space-6);
    }

    li {
        margin-bottom: var(--space-2);
        line-height: 1.6;
    }

    /* Images */
    img {
        max-width: 100%;
        height: auto;
        border-radius: var(--radius-md);
    }

    /* Utility Classes */
    .container {
        width: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
        padding: 0 var(--container-padding);
    }

    .text-center {
        text-align: center;
    }

    .text-left {
        text-align: left;
    }

    .text-right {
        text-align: right;
    }

    .visually-hidden {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }

    /* Smooth animations */
    * {
        transition-property: color, background-color, border-color, transform, opacity;
        transition-duration: var(--transition-fast);
        transition-timing-function: ease-in-out;
    }

    /* Ant Design Overrides */
    .ant-drawer-content-wrapper {
        width: 320px !important;
    }

    .ant-drawer-body {
        display: flex;
        flex-direction: column;
        padding: var(--space-6);
        background-color: var(--gray-50);
    }

    .ant-row-space-between {
        align-items: center;
    }

    /* Enhanced Accessibility Features */
    
    /* Focus management for accessibility */
    :focus {
        outline: 2px solid var(--accent-color);
        outline-offset: 2px;
        border-radius: var(--radius-sm);
    }

    /* Focus visible for keyboard navigation */
    :focus-visible {
        outline: 3px solid var(--accent-color);
        outline-offset: 2px;
        box-shadow: 0 0 0 5px rgba(96, 165, 250, 0.3);
    }

    /* Skip link for screen readers */
    .skip-link {
        position: absolute;
        top: -40px;
        left: 6px;
        background: var(--primary-color);
        color: white;
        padding: var(--space-2) var(--space-4);
        text-decoration: none;
        border-radius: var(--radius-md);
        z-index: 1000;
        transition: top var(--transition-fast);
    }

    .skip-link:focus {
        top: 6px;
    }

    /* High contrast mode support */
    @media (prefers-contrast: high) {
        :root {
            --primary-color: #000000;
            --gray-600: #000000;
            --gray-700: #000000;
        }
    }

    /* Reduced motion support */
    @media (prefers-reduced-motion: reduce) {
        *,
        *::before,
        *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
        }
    }

    /* Screen reader only class */
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }

    /* Ensure minimum touch target size */
    button,
    a,
    input,
    select,
    textarea {
        min-height: 44px;
        min-width: 44px;
    }

    /* High contrast button states */
    button:hover,
    button:focus {
        border: 2px solid currentColor;
    }

    /* Accessible color contrast improvements */
    .text-contrast {
        color: var(--gray-900);
    }

    /* ARIA live regions */
    .live-region {
        position: absolute;
        left: -10000px;
        width: 1px;
        height: 1px;
        overflow: hidden;
    }

    /* Enhanced Mobile Responsive Styles */
    .impact-grid {
        @media (max-width: 1024px) {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: var(--space-4) !important;
        }
        
        @media (max-width: 640px) {
            grid-template-columns: repeat(1, 1fr) !important;
            gap: var(--space-4) !important;
        }
    }

    /* Mobile-optimized container padding */
    @media (max-width: 768px) {
        .container {
            padding: 0 var(--space-4);
        }
        
        section {
            padding: var(--space-16) 0 !important;
        }
    }

    @media (max-width: 480px) {
        section {
            padding: var(--space-12) 0 !important;
        }
        
        .container {
            padding: 0 var(--space-3);
        }
    }

    /* Enhanced button mobile styles */
    @media (max-width: 768px) {
        button {
            width: 100%;
            min-height: 48px;
            font-size: var(--text-base) !important;
        }
    }

    /* Grid responsive improvements */
    @media (max-width: 1024px) {
        div[style*="grid-template-columns"] {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)) !important;
        }
    }

    @media (max-width: 640px) {
        div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
        }
    }

    /* Careers: once roles stack in one column, a sticky index would cover the role details */
    @media (max-width: 640px) {
        .careers-roles-index {
            position: static !important;
        }
    }

    /* Floating Animation for Hero Elements */
    @keyframes float {
        0%, 100% {
            transform: translateY(0px) rotate(0deg);
        }
        50% {
            transform: translateY(-20px) rotate(10deg);
        }
    }

    /* Gradient Animation for Dynamic Background */
    @keyframes gradientShift {
        0% {
            background-position: 0% 50%;
        }
        50% {
            background-position: 100% 50%;
        }
        100% {
            background-position: 0% 50%;
        }
    }

    /* Improve text rendering */
    body {
        text-rendering: optimizeLegibility;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }
`;
